import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSetAtom } from "jotai";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faPlus } from "@fortawesome/free-solid-svg-icons";
import { financialApplicationIdAtomWithStorage } from "@/atoms/financialApplicationIdAtom";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import { FinancialApplicationCoreData } from "@/types/FinancialApplication";
import { AppToasterV2 } from "@ucl/library";
import { RequireAuth } from "@/lib/auth/RequireAuth";
import "./ConsumerPortalApplicationsPage.scss";

export const ConsumerPortalApplicationsPage: React.FC = () => {
  return (
    <RequireAuth clientType="consumer">
      <ConsumerPortalApplicationsPageContent />
    </RequireAuth>
  );
};

const ConsumerPortalApplicationsPageContent: React.FC = () => {
  const navigate = useNavigate();
  const setFinancialApplicationId = useSetAtom(financialApplicationIdAtomWithStorage);
  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);

  const [applications, setApplications] = useState<FinancialApplicationCoreData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUserApplications();
  }, []);

  const fetchUserApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const userApplications = await financialApplicationApiClient.getUserApplications();
      setApplications(userApplications);
    } catch (err) {
      console.error("Failed to fetch user applications:", err);
      setError("Failed to load your applications. Please try again.");
      AppToasterV2.showFailedActionToaster("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const handleStartNewApplication = () => {
    // Clear the financial application ID to force creation of a new one
    setFinancialApplicationId(undefined);
    // Navigate to the home page
    navigate("/");
  };

  const handleBackToPortal = () => {
    navigate("/customer/portal");
  };

  const handleViewApplication = (applicationId: string) => {
    setFinancialApplicationId(applicationId);
    navigate(`/customer/portal/applications/${applicationId}`);
  };

  const handleContinueApplication = (applicationId: string) => {
    setFinancialApplicationId(applicationId);
    navigate("/step/about-you/basic-info");
  };

  const getApplicationStatus = (_application: FinancialApplicationCoreData) => {
    return "Draft";
  };

  const getApplicationType = (_application: FinancialApplicationCoreData) => {
    return "Financial Application";
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="consumer-portal-applications-page">
        <div className="consumer-portal-applications-page__loading">
          <div className="consumer-portal-applications-page__loading-spinner"></div>
          <p>Loading your applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="consumer-portal-applications-page">
      <div className="consumer-portal-applications-page__header">
        <div className="consumer-portal-applications-page__header-content">
          <div className="consumer-portal-applications-page__welcome">
            <h1 className="consumer-portal-applications-page__title">My Applications</h1>
            <p className="consumer-portal-applications-page__subtitle">
              View and manage your financial applications
            </p>
          </div>
          <div className="consumer-portal-applications-page__header-actions">
            <button
              className="consumer-portal-applications-page__header-button consumer-portal-applications-page__header-button--secondary"
              onClick={handleBackToPortal}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Portal
            </button>
            <button
              className="consumer-portal-applications-page__header-button consumer-portal-applications-page__header-button--primary"
              onClick={handleStartNewApplication}
            >
              <FontAwesomeIcon icon={faPlus} />
              Start New Application
            </button>
          </div>
        </div>
      </div>

      <div className="consumer-portal-applications-page__content">
        <div className="consumer-portal-applications-page__breadcrumb">
          <button
            className="consumer-portal-applications-page__breadcrumb-link"
            onClick={handleBackToPortal}
          >
            Consumer Portal
          </button>
          <span className="consumer-portal-applications-page__breadcrumb-separator">/</span>
          <span className="consumer-portal-applications-page__breadcrumb-current">
            My Applications
          </span>
        </div>

        {error && (
          <div className="consumer-portal-applications-page__error">
            <p>{error}</p>
            <button onClick={fetchUserApplications}>Try Again</button>
          </div>
        )}

        {applications.length === 0 && !error && (
          <div className="consumer-portal-applications-page__empty-state">
            <div className="consumer-portal-applications-page__empty-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.67157 3 11 3H13C14.3284 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3>No Applications Yet</h3>
            <p>
              You haven't started any applications yet. Get started by creating your first
              application.
            </p>
            <button
              className="consumer-portal-applications-page__empty-action-button"
              onClick={handleStartNewApplication}
            >
              Start Your First Application
            </button>
          </div>
        )}

        {applications.length > 0 && (
          <div className="consumer-portal-applications-page__applications-grid">
            {applications
              .filter(
                (application) =>
                  application.depositApplications.filter((dep) => dep.isActive).length > 0
              )
              .map((application) => (
                <div
                  key={application.id}
                  className="consumer-portal-applications-page__application-card"
                  onClick={() => handleViewApplication(application.id)}
                >
                  <div className="consumer-portal-applications-page__card-header">
                    <div className="consumer-portal-applications-page__card-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.67157 3 11 3H13C14.3284 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="consumer-portal-applications-page__card-status">
                      <span
                        className={`consumer-portal-applications-page__status-badge consumer-portal-applications-page__status-badge--${getApplicationStatus(application).toLowerCase()}`}
                      >
                        {getApplicationStatus(application)}
                      </span>
                    </div>
                  </div>

                  <div className="consumer-portal-applications-page__card-content">
                    <h3 className="consumer-portal-applications-page__card-title">
                      {getApplicationType(application)}
                    </h3>
                    <p className="consumer-portal-applications-page__card-subtitle">
                      Application #{application.friendlyId}
                    </p>

                    <div className="consumer-portal-applications-page__card-details">
                      <div className="consumer-portal-applications-page__detail-item">
                        <span className="consumer-portal-applications-page__detail-label">
                          Created:
                        </span>
                        <span className="consumer-portal-applications-page__detail-value">
                          {formatDate(application.createdAt)}
                        </span>
                      </div>
                      <div className="consumer-portal-applications-page__detail-item">
                        <span className="consumer-portal-applications-page__detail-label">
                          Last Updated:
                        </span>
                        <span className="consumer-portal-applications-page__detail-value">
                          {formatDate(application.updatedAt || application.modifiedAt)}
                        </span>
                      </div>
                      {application.depositApplications &&
                        application.depositApplications.length > 0 && (
                          <div className="consumer-portal-applications-page__detail-item">
                            <span className="consumer-portal-applications-page__detail-label">
                              Products:
                            </span>
                            <span className="consumer-portal-applications-page__detail-value">
                              {application.depositApplications.filter((dep) => dep.isActive).length}{" "}
                              selected
                            </span>
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="consumer-portal-applications-page__card-actions">
                    <button 
                      className="consumer-portal-applications-page__continue-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleContinueApplication(application.id);
                      }}
                    >
                      Continue Application
                    </button>
                    <button 
                      className="consumer-portal-applications-page__view-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewApplication(application.id);
                      }}
                    >
                      View Application
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsumerPortalApplicationsPage;
